import React, { useEffect, useRef, useState } from 'react';
import './PhoneInput.css';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { Countries, type Country, type MSISDN, isValidMsisdn } from '../../core/util/msisdn';
import { useAppContext } from '../../core/context';
import { getSelectableCountries } from '../../core/context/claims';
import CountrySelector from './CountrySelector';
import rememberMeStorage from '../../lib/clients/remember-me-storage';
import RememberMe from './RememberMe';
import { useLanguageContext } from '../../core/locale/LanguageContext';
import { useIframe } from '../../platform/iframe/useIframe';
import { useDevice } from '../../platform/device/useDevice';
import { canAttempt as canAttemptStorageAccess } from '../../lib/identity/storageAccess';
import { primeStorageAccess } from '../../lib/identity/crossDomainIdentity';
import { isCrossDomainRememberMeEnabled } from '../../lib/identity/featureFlags';
import { isAutofillChange, type PhoneNumberSource } from '../../core/util/phoneNumberSource';

interface PhoneInputProps {
  error?: string | null;
  initialMsisdn: MSISDN;
  loading: boolean;
  noEdit: boolean;
  onSubmit: (m: MSISDN, rememberMe: boolean, numberSource: PhoneNumberSource) => void;
  onUpdateCountry: (country: Countries) => void;
  initialRememberMe?: boolean;
  initialNumberSource: PhoneNumberSource;
}

const ids = {
  phoneNumber: 'phoneNumber',
  phoneNumberError: 'phoneNumber-error',
};

function PhoneInput({
  error,
  initialMsisdn,
  loading,
  noEdit,
  onSubmit,
  onUpdateCountry,
  initialRememberMe,
  initialNumberSource,
}: PhoneInputProps): React.ReactNode {
  const context = useAppContext();
  const iframe = useIframe();
  const device = useDevice();
  const languageContext = useLanguageContext();

  const canOfferRememberMe = (): boolean =>
    !iframe.isEmbedded() || (isCrossDomainRememberMeEnabled() && canAttemptStorageAccess());
  const selectableCountries = getSelectableCountries(context.claims);
  const [msisdn, setMsisdn] = useState<MSISDN>(initialMsisdn);
  const [rememberMe, setRememberMe] = useState<boolean>(
    initialRememberMe ?? rememberMeStorage.exists()
  );
  const disabled = !isValidMsisdn(msisdn);
  const inputRef = useRef<HTMLInputElement>(null);
  const userEditedRef = useRef(false);
  const rememberMeToggledRef = useRef(false);
  const editedNumberSourceRef = useRef<PhoneNumberSource | null>(null);

  useEffect(() => {
    if (!userEditedRef.current) {
      setMsisdn(initialMsisdn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- track the value, not the object ref
  }, [initialMsisdn.number, initialMsisdn.country.country]);

  useEffect(() => {
    if (!rememberMeToggledRef.current && initialRememberMe !== undefined) {
      setRememberMe(initialRememberMe);
    }
  }, [initialRememberMe]);

  const onInternalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/\s/g, '');
    if (Number(input) || input == '') {
      userEditedRef.current = true;
      editedNumberSourceRef.current = isAutofillChange(e) ? 'autofill' : 'manual';
      setMsisdn({ ...msisdn, number: input });
    }
  };

  const onChangeRememberMe = (value: boolean) => {
    rememberMeToggledRef.current = true;
    setRememberMe(value);
  };

  const onInternalSubmit = (event: React.KeyboardEvent | React.MouseEvent) => {
    const isMouseEvent = event.type === 'click';
    const isKeyboardEvent = !isMouseEvent;

    if (isKeyboardEvent && (event as React.KeyboardEvent).key !== 'Enter') return;
    if (isMouseEvent && (event as React.MouseEvent).button !== 0) return;
    primeStorageAccess();
    onSubmit(msisdn, rememberMe, editedNumberSourceRef.current ?? initialNumberSource);
  };

  const onChangeCountry = (country: Country) => {
    if (msisdn.country !== country) {
      userEditedRef.current = true;
      setMsisdn({ ...msisdn, country });
      onUpdateCountry(country.country);
    }

    inputRef.current?.focus();
  };

  return (
    <div className="input-frame">
      <div className="phone-input-and-picker">
        <div className={`phone-input ${noEdit ? 'disabled' : ''}`}>
          <CountrySelector
            disabled={noEdit}
            onChangeCountry={onChangeCountry}
            country={msisdn.country}
            countries={selectableCountries}
          />
          <input
            type="tel"
            name="phone-number"
            className="phone-number-input"
            data-testid="phone-number-input"
            aria-label={languageContext.getLocalizedText('inputInputLabel')}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? ids.phoneNumberError : undefined}
            value={msisdn.number || ''} // improvement: use prettyNumber here but needs extra logic to handle delete key
            placeholder={languageContext.getLocalizedText('inputPlaceholder')}
            onKeyDown={onInternalSubmit}
            onChange={onInternalChange}
            inputMode="numeric"
            pattern={msisdn.country.regex}
            minLength={msisdn.country.minlength}
            maxLength={msisdn.country.maxlength}
            autoComplete="tel-national"
            required
            // eslint-disable-next-line jsx-a11y/no-autofocus -- primary input on the page; focusing it is intentional UX
            autoFocus
            readOnly={noEdit}
            ref={inputRef}
          />
        </div>

        {error ?
          <p id={ids.phoneNumberError} role="alert" className={`error ${noEdit ? 'disabled' : ''}`}>
            {error}
          </p>
        : null}
        <div className="phone-input-footer">
          {!device.isMobile && !noEdit && canOfferRememberMe() && (
            <RememberMe value={rememberMe} onChange={onChangeRememberMe} />
          )}
          <div className="phone-input-footer-btn">
            <PrimaryButton
              disabled={disabled}
              loading={loading}
              onClick={onInternalSubmit}
              aria-label={languageContext.getLocalizedText('inputPrimaryLabel')}
            >
              {languageContext.getLocalizedText('inputPrimary')}
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PhoneInput;
